import { PaymentInstructionsResponse } from '@/DTOs/paymentInstruction';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';

export type PaymentField =
  | 'beneficiaryName'
  | 'beneficiaryAddress'
  | 'bankInfo'
  | 'accountNumber'
  | 'routingNumber'
  | 'swiftCode'
  | 'reference';

export const FIELDS_BY_RAIL: Readonly<Record<string, readonly PaymentField[]>> = {
  [PaymentRailType.ACH]: ['beneficiaryName', 'bankInfo', 'accountNumber', 'routingNumber'],
  [PaymentRailType.Wire]: ['beneficiaryName', 'bankInfo', 'accountNumber', 'routingNumber'],
  [PaymentRailType.RTP]: ['beneficiaryName', 'accountNumber', 'routingNumber'],
  [PaymentRailType.Swift]: [
    'beneficiaryName',
    'beneficiaryAddress',
    'bankInfo',
    'accountNumber',
    'swiftCode',
  ],
};

export const ALL_FIELDS: readonly PaymentField[] = [
  'beneficiaryName',
  'beneficiaryAddress',
  'bankInfo',
  'accountNumber',
  'routingNumber',
  'swiftCode',
  'reference',
];

export interface FormatPaymentInstructionsOptions {
  readonly instructions: PaymentInstructionsResponse;
  readonly paymentRail: PaymentRailType;
  readonly tr: (key: string, ...args: unknown[]) => string;
  readonly fromAmount?: number | null;
  readonly fromCurrency?: CurrencyCodeType;
  readonly toAmount?: number | null;
  readonly toCurrency?: CurrencyCodeType;
  readonly formatAmount?: (amount: number, currency: string) => string;
}

export const formatPaymentInstructions = (
  instructionsOrOptions: PaymentInstructionsResponse | FormatPaymentInstructionsOptions,
  paymentRail?: PaymentRailType,
  tr?: (key: string, ...args: unknown[]) => string,
): string => {
  // Support both old and new call signatures
  const options: FormatPaymentInstructionsOptions =
    'instructions' in instructionsOrOptions
      ? instructionsOrOptions
      : {
          instructions: instructionsOrOptions,
          paymentRail: paymentRail!,
          tr: tr!,
        };

  const fields = FIELDS_BY_RAIL[options.paymentRail] ?? ALL_FIELDS;
  const lines: string[] = [];

  for (const field of fields) {
    switch (field) {
      case 'beneficiaryName':
        lines.push(`${options.tr('BENEFICIARY_NAME')}: ${options.instructions.receiverName}`);
        break;
      case 'beneficiaryAddress':
        if (options.instructions.receiverAddress) {
          lines.push(
            `${options.tr('BENEFICIARY_ADDRESS')}: ${options.instructions.receiverAddress}`,
          );
        }
        break;
      case 'bankInfo':
        lines.push(`${options.tr('BANK_INFORMATION')}:`);
        lines.push(`  ${options.instructions.receiverBankName}`);
        lines.push(`  ${options.instructions.receiverBankAddress}`);
        break;
      case 'accountNumber':
        lines.push(
          `${options.tr('ACCOUNT_NUMBER', CurrencyCodeType.USD)}: ${options.instructions.accountNumber}`,
        );
        break;
      case 'routingNumber':
        lines.push(
          `${options.tr('ROUTING_NUMBER', CurrencyCodeType.USD)}: ${options.instructions.routingNumber}`,
        );
        break;
      case 'swiftCode':
        if (options.instructions.swiftCode) {
          lines.push(`${options.tr('SWIFT_CODE')}: ${options.instructions.swiftCode}`);
        }
        break;
      case 'reference':
        if (options.instructions.reference) {
          lines.push(`${options.tr('REFERENCE')}: ${options.instructions.reference}`);
        }
        break;
    }
  }

  // Add amount information if provided
  if (
    options.fromAmount != null &&
    options.fromCurrency != null &&
    options.toAmount != null &&
    options.toCurrency != null &&
    options.formatAmount
  ) {
    lines.push('');
    lines.push(
      `${options.tr('YOU_SEND')}: ${options.formatAmount(options.fromAmount, options.fromCurrency)}`,
    );
    lines.push(
      `${options.tr('YOU_RECEIVE')}: ${options.formatAmount(options.toAmount, options.toCurrency)}`,
    );
  }

  return lines.filter(Boolean).join('\n');
};
